import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useApi } from "../../../containers/ApiContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";

type Props = {};

interface TailNumberEntry {
  tail_number: string;
  manufacturer: string;
  model: string;
  created_at?: string;
  added_by_user_id?: string | null;
  users?: {
    email: string;
  } | null;
}

interface FormData {
  tailNumber: string;
  manufacturer: string;
  model: string;
}

interface FormErrors {
  tailNumber?: string;
  manufacturer?: string;
  model?: string;
}

export const TailMappingTab: React.FC<Props> = () => {
  const { postgrest } = useApi();
  const { identity, airplxIdentity } = useIdentity();
  const snackbar = useSnackbar();

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    tailNumber: "",
    manufacturer: "",
    model: "",
  });

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [recentlyAdded, setRecentlyAdded] = React.useState<TailNumberEntry[]>(
    []
  );

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");
  const [tailNumberToDelete, setTailNumberToDelete] = React.useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Tail number validation
    const tailNumber = formData.tailNumber.trim().toUpperCase();
    if (!tailNumber) {
      newErrors.tailNumber = "Tail number is required";
    } else if (!/^[A-Z0-9\-]+$/.test(tailNumber)) {
      newErrors.tailNumber =
        "Tail number must contain only letters, numbers, and hyphens";
    } else if (tailNumber.length < 2 || tailNumber.length > 10) {
      newErrors.tailNumber = "Tail number must be between 2 and 10 characters";
    }

    // Manufacturer validation
    const manufacturer = formData.manufacturer.trim();
    if (!manufacturer) {
      newErrors.manufacturer = "Manufacturer is required";
    } else if (manufacturer.length < 2 || manufacturer.length > 100) {
      newErrors.manufacturer =
        "Manufacturer must be between 2 and 100 characters";
    }

    // Model validation
    const model = formData.model.trim();
    if (!model) {
      newErrors.model = "Model is required";
    } else if (model.length < 1 || model.length > 100) {
      newErrors.model = "Model must be between 1 and 100 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleFieldChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear error for this field when user types
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tailNumber = formData.tailNumber.trim().toUpperCase();
      const manufacturer = formData.manufacturer.trim().toUpperCase();
      const model = formData.model.trim().toUpperCase();

      // Check if tail number already exists
      const { data: existingData, error: checkError } = await postgrest
        .from("faa_tail_numbers")
        .select("tail_number")
        .eq("tail_number", tailNumber)
        .single();

      if (existingData && !checkError) {
        snackbar.notify({
          text: `Tail number ${tailNumber} already exists in the database`,
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      // Insert new tail number with current user ID
      const { data: insertData, error: insertError } = await postgrest
        .from("faa_tail_numbers")
        .insert({
          tail_number: tailNumber,
          manufacturer: manufacturer,
          model: model,
          added_by_user_id: airplxIdentity.userid || null, // Set current user's ID
        })
        .select("*, users!faa_tail_numbers_added_by_user_id_fkey(email)")
        .single();

      if (insertError) {
        // Handle specific error cases
        if (insertError.code === "23505") {
          // Unique constraint violation
          snackbar.notify({
            text: `Tail number ${tailNumber} already exists`,
            severity: "error",
          });
        } else if (insertError.code === "42501") {
          // Permission denied
          snackbar.notify({
            text: "You don't have permission to add tail numbers",
            severity: "error",
          });
        } else {
          snackbar.notify({
            text: `Failed to add tail number: ${insertError.message}`,
            severity: "error",
          });
        }
      } else {
        // Success
        snackbar.notify({
          text: `Successfully added tail number ${tailNumber}`,
          severity: "success",
        });

        // Add to recently added list with user information
        setRecentlyAdded((prev) => [
          {
            tail_number: tailNumber,
            manufacturer: manufacturer,
            model: model,
            created_at: new Date().toISOString(),
            added_by_user_id: insertData?.added_by_user_id || null,
            users: insertData?.users || null,
          },
          ...prev.slice(0, 9), // Keep only last 10 entries
        ]);

        // Clear form
        setFormData({
          tailNumber: "",
          manufacturer: "",
          model: "",
        });
      }
    } catch (error) {
      console.error("Error adding tail number:", error);
      snackbar.notify({
        text:
          error instanceof Error
            ? `Error: ${error.message}`
            : "An unexpected error occurred",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (tailNumber: string) => {
    setTailNumberToDelete(tailNumber);
    setDeleteDialogOpen(true);
    setDeleteConfirmText("");
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!tailNumberToDelete || deleteConfirmText.toLowerCase() !== "delete") {
      return;
    }

    setIsDeleting(true);

    try {
      const { error } = await postgrest
        .from("faa_tail_numbers")
        .delete()
        .eq("tail_number", tailNumberToDelete);

      if (error) {
        snackbar.notify({
          text: `Failed to delete tail number: ${error.message}`,
          severity: "error",
        });
      } else {
        snackbar.notify({
          text: `Successfully deleted tail number ${tailNumberToDelete}`,
          severity: "success",
        });

        // Remove from recently added list
        setRecentlyAdded((prev) =>
          prev.filter((entry) => entry.tail_number !== tailNumberToDelete)
        );
      }
    } catch (error) {
      console.error("Error deleting tail number:", error);
      snackbar.notify({
        text:
          error instanceof Error
            ? `Error: ${error.message}`
            : "An unexpected error occurred while deleting",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTailNumberToDelete(null);
      setDeleteConfirmText("");
    }
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTailNumberToDelete(null);
    setDeleteConfirmText("");
  };

  // Load recently added tail numbers on mount
  React.useEffect(() => {
    const loadRecentEntries = async () => {
      try {
        const { data, error } = await postgrest
          .from("faa_tail_numbers")
          .select(
            "tail_number, manufacturer, model, created_at, added_by_user_id, users!faa_tail_numbers_added_by_user_id_fkey(email)"
          )
          .order("created_at", { ascending: false })
          .limit(10);

        if (data && !error) {
          setRecentlyAdded(data);
        }
      } catch (error) {
        console.error("Error loading recent entries:", error);
      }
    };

    loadRecentEntries();
  }, [postgrest]);

  return (
    <Stack direction="column" spacing={3} sx={{ p: 2, height: "100%" }}>
      {/* Header */}
      <Typography variant="h6" color="text.primary">
        Manually Add FAA Tail Number
      </Typography>

      {/* Form Card */}
      <Card elevation={2}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Add a new tail number to the FAA database. This is useful for
                aircraft not yet in the FAA registry.
              </Typography>

              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label="Tail Number"
                  placeholder="N12345"
                  value={formData.tailNumber}
                  onChange={handleFieldChange("tailNumber")}
                  error={!!errors.tailNumber}
                  helperText={errors.tailNumber}
                  size="small"
                  sx={{ width: 200 }}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 10,
                    style: { textTransform: "uppercase" },
                  }}
                />

                <TextField
                  label="Manufacturer"
                  placeholder="CESSNA"
                  value={formData.manufacturer}
                  onChange={handleFieldChange("manufacturer")}
                  error={!!errors.manufacturer}
                  helperText={errors.manufacturer}
                  size="small"
                  sx={{ width: 250 }}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 100,
                    style: { textTransform: "uppercase" },
                  }}
                />

                <TextField
                  label="Model"
                  placeholder="172S"
                  value={formData.model}
                  onChange={handleFieldChange("model")}
                  error={!!errors.model}
                  helperText={errors.model}
                  size="small"
                  sx={{ width: 250 }}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 100,
                    style: { textTransform: "uppercase" },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : <AddIcon />
                  }
                  disabled={isSubmitting}
                  sx={{ height: 40, mt: 0.5 }}
                >
                  {isSubmitting ? "Adding..." : "Add Tail Number"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Recently Added Section */}
      <Paper
        elevation={1}
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Recently Added Tail Numbers
          </Typography>
        </Box>

        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  Tail Number
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  Manufacturer
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  Model
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  Source
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  Added
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                    width: 80,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentlyAdded.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No recently added tail numbers
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentlyAdded.map((entry) => (
                  <TableRow key={entry.tail_number}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {entry.tail_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{entry.manufacturer}</TableCell>
                    <TableCell>{entry.model}</TableCell>
                    <TableCell>
                      {entry.added_by_user_id && entry.users?.email ? (
                        <Typography variant="body2" color="text.primary">
                          {entry.users.email}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          FAA Import
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleString("en-US", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "Just now"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(entry.tail_number)}
                        sx={{ color: "error.main" }}
                        title={`Delete tail number ${entry.tail_number}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Tail Number</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete tail number{" "}
            <strong>{tailNumberToDelete}</strong>? This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm deletion, please type <strong>"delete"</strong> in the
            field below:
          </DialogContentText>
          <TextField
            fullWidth
            placeholder="Type 'delete' to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            size="small"
            error={
              deleteConfirmText !== "" &&
              deleteConfirmText.toLowerCase() !== "delete"
            }
            helperText={
              deleteConfirmText !== "" &&
              deleteConfirmText.toLowerCase() !== "delete"
                ? "Please type 'delete' exactly"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={
              isDeleting || deleteConfirmText.toLowerCase() !== "delete"
            }
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
